import { getReasoningText } from '@/lib'
import type { UseChatHelpers } from '@ai-sdk/react'
import type { ChatStoreRequest } from '@jobilla/nexus-sdk'
import { UIMessage } from 'ai'
import { useEffect, useRef } from 'react'

// Allow one automatic continuation per user turn.
// Continuation requests run with tools disabled server-side so the assistant
// can produce natural follow-up text without triggering a second mutation tool.
const MAX_AUTO_CONTINUATIONS_PER_USER_TURN = 1

type UseAutoSendContinuationParams = {
	messages: UIMessage[]
	isLoadingChatHistory: boolean
	status: string
	hasPendingToolCalls: boolean
	isResettingChat: boolean
	campaignId: string
	buildExtraData: () => Record<string, unknown>
	originalSendMessage: UseChatHelpers<UIMessage>['sendMessage']
	getAccessToken: () => Promise<string | null>
}

type MessagePart = {
	type: string
	text?: string
	state?: string
	[key: string]: unknown
}

/**
 * Helper to check if a part is a tool part (but not a tool-result)
 */
const isToolPart = (part: MessagePart): boolean => {
	return typeof part.type === 'string' && part.type.startsWith('tool-') && part.type !== 'tool-result'
}

/**
 * Helper to check if a tool part is in a terminal (completed) state
 */
const isTerminalToolState = (part: MessagePart): boolean => {
	return part.state === 'output-available' || part.state === 'output-error'
}

/**
 * Check if there's meaningful text AFTER the last completed tool part.
 * Intro text before tools (e.g. "Let me check that for you!") should NOT
 * prevent continuation — the AI still needs to respond with actual results.
 */
export const hasMeaningfulTextAfterTools = (parts: MessagePart[]): boolean => {
	// Find the index of the last completed tool part
	let lastToolIndex = -1
	for (let i = parts.length - 1; i >= 0; i--) {
		if (isToolPart(parts[i]) && isTerminalToolState(parts[i])) {
			lastToolIndex = i
			break
		}
	}

	// No completed tools → check all parts (original behavior)
	const partsToCheck = lastToolIndex === -1 ? parts : parts.slice(lastToolIndex + 1)

	return partsToCheck.some(part => {
		if (part.type === 'text') {
			return part.text && part.text.trim().length > 0
		}
		if (part.type === 'reasoning') {
			return getReasoningText(part).trim().length > 0
		}
		return false
	})
}

const isToolOnlyCompletedAssistantMessage = (message: UIMessage): boolean => {
	if (message.role !== 'assistant') {
		return false
	}

	const parts = (message.parts || []) as MessagePart[]
	const hasCompletedTools = parts.some(part => isToolPart(part) && isTerminalToolState(part))
	if (!hasCompletedTools) {
		return false
	}

	return !hasMeaningfulTextAfterTools(parts)
}

/**
 * Hook that automatically sends a continuation request when the assistant's last message
 * has completed tool calls with no text content after them.
 *
 * This enables a conversation flow where the assistant can:
 * 1. Call tools
 * 2. Wait for them to complete
 * 3. Automatically continue with a response based on the tool results
 */
export function useAutoSendContinuation({
	messages,
	isLoadingChatHistory,
	status,
	hasPendingToolCalls,
	isResettingChat,
	campaignId,
	buildExtraData,
	originalSendMessage,
	getAccessToken,
}: UseAutoSendContinuationParams) {
	const autoSentForMessageIdRef = useRef<string | null>(null)

	useEffect(() => {
		// Hard guard: never auto-continue while chat reset is running
		if (isResettingChat) {
			return
		}

		// Don't auto-send while chat is still loading history or actively processing
		if (isLoadingChatHistory || status === 'submitted' || status === 'streaming') {
			return
		}

		// Don't auto-send if there are still pending tools
		if (hasPendingToolCalls) {
			return
		}

		// Reset continuation tracking when the chat is emptied
		if (messages.length === 0) {
			autoSentForMessageIdRef.current = null
			return
		}

		const lastMessage = messages[messages.length - 1]

		// Only auto-send for assistant messages
		if (!lastMessage || lastMessage.role !== 'assistant') {
			return
		}

		// Check if we already auto-sent for this message
		if (autoSentForMessageIdRef.current === lastMessage.id) {
			return
		}

		// Circuit breaker: stop after N automatic continuations in the current user turn
		const lastUserIndex = messages.map(message => message.role).lastIndexOf('user')
		const assistantMessagesAfterLastUser = messages.slice(lastUserIndex + 1)
		const toolOnlyCompletedAssistantCount = assistantMessagesAfterLastUser
			.filter(isToolOnlyCompletedAssistantMessage)
			.length
		const autoContinuationsAlreadyUsed = Math.max(0, toolOnlyCompletedAssistantCount - 1)
		if (autoContinuationsAlreadyUsed >= MAX_AUTO_CONTINUATIONS_PER_USER_TURN) {
			return
		}

		const parts = (lastMessage.parts || []) as MessagePart[]

		// Continue only when at least one tool succeeded and none failed.
		const hasSuccessfulTools = parts.some(part => isToolPart(part) && part.state === 'output-available')
		const hasFailedTools = parts.some(part => isToolPart(part) && part.state === 'output-error')

		if (!hasSuccessfulTools || hasFailedTools) {
			return
		}

		// If the message already contains meaningful text AFTER tools,
		// the assistant has already responded to tool results — no continuation needed.
		// Intro text before tools (e.g. "Let me check that!") doesn't count.
		if (hasMeaningfulTextAfterTools(parts)) {
			return
		}

		// All conditions met - auto-send continuation
		autoSentForMessageIdRef.current = lastMessage.id

		const extraData = buildExtraData()
		getAccessToken()
			.then(token => {
				if (!token) {
					console.warn('[useAutoSendContinuation] No auth token available, aborting continuation')
					autoSentForMessageIdRef.current = null
					return
				}
				return originalSendMessage(undefined, {
					headers: {
						'Accept': 'application/json',
						'Content-Type': 'application/json',
						'Authorization': `Bearer ${token}`,
					},
					body: {
						...({ campaignId } satisfies Pick<ChatStoreRequest, 'campaignId'>),
						extraData,
						meta: {
							disableTools: true,
							disable_tools: true,
						},
					},
				})
			})
			.catch(error => {
				console.error('[useAutoSendContinuation] Failed to send continuation:', error)
				// Reset the ref so the effect can retry on the next render cycle
				autoSentForMessageIdRef.current = null
			})
	}, [
		messages,
		isLoadingChatHistory,
		status,
		hasPendingToolCalls,
		isResettingChat,
		campaignId,
		buildExtraData,
		originalSendMessage,
		getAccessToken,
	])
}
